import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';

import { UtilitiesService } from '../../../services/utilities.service';
import { SnackbarType } from '../../components/snackbar/snackbar.component';

const DEFAULT_ERROR_MESSAGE = 'An error occurred';

type ApiErrorBody = {
	Message?: string;
	message?: string;
	errors?: Array<{ message?: string }>;
}

export const httpErrorsInterceptor: HttpInterceptorFn = (req, next) => {
	const utilitiesService = inject(UtilitiesService);

	if (shouldSkipErrorSnackbar(req)) {
		return next(req);
	}

	return next(req).pipe(
		catchError((err: unknown) => {
			if (!(err instanceof HttpErrorResponse) || !shouldShowErrorSnackbar(err)) {
				return throwError(() => err);
			}

			return from(parseHttpErrorBody(err)).pipe(
				switchMap((body) => {
					utilitiesService.showSnackbar(extractServerMessage(body) ?? DEFAULT_ERROR_MESSAGE, SnackbarType.Error);
					return throwError(() => err);
				})
			);
		})
	);
};

function shouldSkipErrorSnackbar(req: HttpRequest<unknown>): boolean {
	return req.url.includes('/graphql') || req.params.get('fromAuthGuard') === 'true';
}

function shouldShowErrorSnackbar(err: HttpErrorResponse): boolean {
	return err.status !== 401 && err.status !== 403;
}

async function parseHttpErrorBody(err: HttpErrorResponse): Promise<ApiErrorBody | string | null> {
	const error = err.error;

	if (error instanceof Blob) {
		try {
			const text = await error.text();
			return JSON.parse(text) as ApiErrorBody;
		} catch {
			return null;
		}
	}

	if (typeof error === 'string') {
		try {
			return JSON.parse(error) as ApiErrorBody;
		} catch {
			return error;
		}
	}

	if (error && typeof error === 'object') {
		return error as ApiErrorBody;
	}

	return null;
}

function extractServerMessage(body: ApiErrorBody | string | null): string | null {
	if (!body) {
		return null;
	}

	if (typeof body === 'string' && body.trim().length > 0) {
		return body;
	}

	if (typeof body === 'object') {
		if (typeof body.Message === 'string' && body.Message.trim().length > 0) {
			return body.Message;
		}

		if (typeof body.message === 'string' && body.message.trim().length > 0) {
			return body.message;
		}

		const firstError = body.errors?.[0];

		if (typeof firstError?.message === 'string' && firstError.message.trim().length > 0) {
			return firstError.message;
		}
	}

	return null;
}
